import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React from 'react'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { Report } from '@/types/report'
import { formatEpoch, formatFileSize } from '@/utils/formatter'

type ReportCard = {
  report: Report
  onClickDownload(xid: string): void
}

const ReportCard: React.FC<ReportCard> = ({ report, onClickDownload }) => {
  const router = useRouter()
  const { t } = useTranslation()

  return (
    <>
      <div className='flex flex-col gap-2 rounded-lg bg-[#FAFAFA] px-[9px] py-2 lg:hidden lg:p-2'>
        <div className='flex gap-3'>
          <div className='flex h-9 w-9 items-center justify-center'>
            <span className='kek-document-bullet-list text-xl text-red' />
          </div>

          <div className='flex flex-col gap-1'>
            <p className='text-sm font-bold leading-normal text-dark'>{report.title}</p>
            <p className='text-xs font-semibold leading-normal text-gray-2'>
              {report.date && formatEpoch(report.date, 'dd LLLL yyyy', String(router.locale))}
            </p>
          </div>
        </div>

        <div className='h-[1px] w-full bg-neutral-border' />

        <div className='flex w-full items-center justify-between'>
          <p className='text-xs font-semibold leading-normal text-gray-1'>
            {formatFileSize(Number(report.file.fileSize))}
          </p>

          <div className='flex gap-2'>
            <Link
              href={report.file.url}
              target='_blank'
              className='flex h-8 w-8 items-center justify-center rounded-full bg-green'
            >
              <span className='kek-eye text-white' />
            </Link>

            <button
              type='button'
              className='flex h-8 w-8 items-center justify-center rounded-full bg-red'
              onClick={() => onClickDownload(report.xid)}
            >
              <span className='kek-arrow-download text-white' />
            </button>
          </div>
        </div>
      </div>

      <div className='group relative hidden h-[384px] overflow-hidden rounded-lg border border-neutral-border bg-white lg:block'>
        <div className='relative w-full rounded-lg p-3'>
          <div className='absolute left-3 top-3 rounded-br-md rounded-tl-md bg-orange p-2.5'>
            <p className='text-sm font-bold leading-normal text-dark'>{report.year}</p>
          </div>
          <div className='absolute inset-0 bg-dark/[0.35] opacity-0 transition-all duration-500 lg:group-hover:opacity-100' />
          <ImageByWidthDevice
            images={report.imageFiles}
            alt={report.title}
            className='h-[300px] w-full rounded-lg object-cover'
          />
        </div>

        <div className='absolute -bottom-3 flex w-full flex-col gap-3.5 overflow-hidden rounded-lg bg-white p-3 transition-all duration-500 lg:group-hover:bottom-0'>
          <h5 className='h-12 min-h-12 font-bold tracking-[-0.176px] text-dark '>{report.title}</h5>

          <div className='flex max-h-0 flex-col gap-3.5 transition-all duration-500 lg:group-hover:max-h-screen'>
            <div className='flex flex-col gap-3.5'>
              <div className='flex items-center gap-1.5'>
                <p className='text-sm leading-normal tracking-[-0.084px] text-gray-1'>Size</p>
                <p className='text-sm leading-normal tracking-[-0.084px] text-gray-1'>:</p>
                <p className='text-sm font-bold leading-normal tracking-[-0.084px] text-gray-1'>
                  {formatFileSize(Number(report.file.fileSize))}
                </p>
              </div>

              <div className='flex items-center gap-1.5'>
                <p className='text-sm leading-normal tracking-[-0.084px] text-gray-1'>Date</p>
                <p className='text-sm leading-normal tracking-[-0.084px] text-gray-1'>:</p>
                <p className='text-sm font-bold leading-normal tracking-[-0.084px] text-gray-1'>
                  {report.date && formatEpoch(report.date, 'dd LLLL yyyy', String(router.locale))}
                </p>
              </div>
            </div>

            <div className='flex flex-col gap-3'>
              <Link
                href={report.file.url}
                target='_blank'
                className='flex items-center justify-between gap-2 rounded-full bg-green px-5 py-2.5'
              >
                <span className='font-bold text-white'>{t('common:preview')}</span>
                <i className='kek-eye text-xl text-white' />
              </Link>

              <button
                type='button'
                className='flex items-center justify-between gap-2 rounded-full bg-red px-5 py-2.5'
                onClick={() => onClickDownload(report.xid)}
              >
                <span className='font-bold text-white'>{t('common:download')}</span>
                <i className='kek-arrow-download text-xl text-white' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ReportCard
